import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Scissors, Mic2, Radio, Brain, Trophy, CalendarClock, Users, Crown, LogOut, PlayCircle, Dna, Globe, Search } from "lucide-react";
import { listShows, deleteShow, remineShow, retryFailed } from "../lib/api";
import { UploadPanel } from "../components/UploadPanel";
import { AiKeyPanel } from "../components/AiKeyPanel";
import { ShowRow } from "../components/ShowRow";
import { ShowView } from "../components/ShowView";
import ProfileView from "./ProfileView";
import BestOfView from "./BestOfView";
import SchedulerView from "./SchedulerView";
import RosterView from "./RosterView";
import { useAuth } from "../contexts/AuthContext";
import { openUpgrade } from "../components/UpgradeModal";
import { toast } from "sonner";

export default function Dashboard() {
  const { user, isPro, logout } = useAuth();
  const [shows, setShows] = useState([]);
  const [selected, setSelected] = useState(null);
  const [view, setView] = useState("show");
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const data = await listShows();
      setShows(data);
      setLoaded(true);
      return data;
    } catch (e) {
      setLoaded(true);
      return [];
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // poll while any show is processing
  useEffect(() => {
    const anyWorking = shows.some((s) => !["completed", "failed"].includes(s.status));
    if (!anyWorking) return;
    const t = setInterval(refresh, 3000);
    return () => clearInterval(t);
  }, [shows, refresh]);

  const onCreated = async (show) => {
    const data = await refresh();
    setSelected(show.id);
    setView("show");
    if (!data.length) setShows([show]);
  };

  const onDelete = async (id) => {
    await deleteShow(id);
    toast.success("Show removed from archive");
    if (selected === id) setSelected(null);
    refresh();
  };

  const selectShow = (id) => { setSelected(id); setView("show"); };

  const onRetry = async (id) => {
    try {
      await remineShow(id);
      toast.success("Retrying — re-downloading and mining");
      refresh();
    } catch {
      toast.error("Retry failed");
    }
  };

  const active = shows.filter((s) => !["completed", "failed"].includes(s.status));
  const failedCount = shows.filter((s) => s.status === "failed").length;

  const retryAll = async () => {
    try {
      const res = await retryFailed();
      toast.success(`Retrying ${res.retried} failed download${res.retried === 1 ? "" : "s"}`);
      refresh();
    } catch {
      toast.error("Retry-all failed");
    }
  };

  return (
    <div className="grain min-h-screen">
      <div className="mx-auto flex min-h-screen max-w-[1600px]">
        {/* Sidebar */}
        <aside className="sticky top-0 flex h-screen w-[340px] shrink-0 flex-col border-r border-border bg-background/80 backdrop-blur">
          <div className="flex items-center gap-3 border-b border-border px-6 py-6">
            <div className="grid h-10 w-10 place-items-center bg-primary">
              <Scissors className="h-5 w-5 text-primary-foreground" />
            </div>
            <div>
              <h1 className="font-display text-2xl leading-none tracking-widest text-white">BARTH</h1>
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-accent">Clip Miner</p>
            </div>
          </div>

          <div className="space-y-2 px-5 pt-5">
            <button
              data-testid="nav-bestof-btn"
              onClick={() => setView("bestof")}
              className={`flex w-full items-center gap-3 border px-4 py-3 text-left transition-colors ${
                view === "bestof" ? "border-accent bg-accent/10 text-accent" : "border-border text-muted-foreground hover:border-accent hover:text-white"
              }`}
            >
              <Trophy className="h-5 w-5" />
              <div>
                <p className="text-sm font-semibold tracking-wide">Best Of</p>
                <p className="font-mono text-[10px] uppercase tracking-widest opacity-70">Top clips, all shows</p>
              </div>
            </button>
            <button
              data-testid="nav-profile-btn"
              onClick={() => setView("profile")}
              className={`flex w-full items-center gap-3 border px-4 py-3 text-left transition-colors ${
                view === "profile" ? "border-accent bg-accent/10 text-accent" : "border-border text-muted-foreground hover:border-accent hover:text-white"
              }`}
            >
              <Brain className="h-5 w-5" />
              <div>
                <p className="text-sm font-semibold tracking-wide">Humor Profile</p>
                <p className="font-mono text-[10px] uppercase tracking-widest opacity-70">Your voice, baked in</p>
              </div>
            </button>
            <button
              data-testid="nav-schedule-btn"
              onClick={() => setView("schedule")}
              className={`flex w-full items-center gap-3 border px-4 py-3 text-left transition-colors ${
                view === "schedule" ? "border-accent bg-accent/10 text-accent" : "border-border text-muted-foreground hover:border-accent hover:text-white"
              }`}
            >
              <CalendarClock className="h-5 w-5" />
              <div>
                <p className="text-sm font-semibold tracking-wide">Schedule</p>
                <p className="font-mono text-[10px] uppercase tracking-widest opacity-70">Plan your posts</p>
              </div>
            </button>
            <button
              data-testid="nav-roster-btn"
              onClick={() => setView("roster")}
              className={`flex w-full items-center gap-3 border px-4 py-3 text-left transition-colors ${
                view === "roster" ? "border-accent bg-accent/10 text-accent" : "border-border text-muted-foreground hover:border-accent hover:text-white"
              }`}
            >
              <Users className="h-5 w-5" />
              <div>
                <p className="text-sm font-semibold tracking-wide">Roster</p>
                <p className="font-mono text-[10px] uppercase tracking-widest opacity-70">Faces you follow</p>
              </div>
            </button>
            <a
              href="/public-settings"
              data-testid="nav-public-site-btn"
              className="flex w-full items-center gap-3 border border-border px-4 py-3 text-left text-muted-foreground transition-colors hover:border-accent hover:text-white"
            >
              <Globe className="h-5 w-5" />
              <div>
                <p className="text-sm font-semibold tracking-wide">Public Site</p>
                <p className="font-mono text-[10px] uppercase tracking-widest opacity-70">Share &amp; go public</p>
              </div>
            </a>
            <a
              href="/discover"
              data-testid="nav-discover-btn"
              className="flex w-full items-center gap-3 border border-border px-4 py-3 text-left text-muted-foreground transition-colors hover:border-accent hover:text-white"
            >
              <Search className="h-5 w-5" />
              <div>
                <p className="text-sm font-semibold tracking-wide">Discover</p>
                <p className="font-mono text-[10px] uppercase tracking-widest opacity-70">Search · guests · chapters</p>
              </div>
            </a>
          </div>

          <div className="p-5 space-y-4">
            <AiKeyPanel />
            <UploadPanel onCreated={onCreated} />
          </div>

          <div className="flex items-center justify-between px-6 pb-2 pt-1">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Archive</p>
            <div className="flex items-center gap-3">
              {failedCount > 0 && (
                <button
                  data-testid="retry-all-btn"
                  onClick={retryAll}
                  className="font-mono text-[10px] uppercase tracking-widest text-primary hover:text-white"
                >
                  Retry {failedCount} failed
                </button>
              )}
              {active.length > 0 && (
                <span className="flex items-center gap-1 font-mono text-[10px] text-accent">
                  <Radio className="h-3 w-3 animate-pulse" /> {active.length} mining
                </span>
              )}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto pb-6" data-testid="archive-list">
            <AnimatePresence>
              {shows.map((s) => (
                <ShowRow
                  key={s.id}
                  show={s}
                  active={view === "show" && selected === s.id}
                  onSelect={selectShow}
                  onDelete={onDelete}
                  onRetry={onRetry}
                />
              ))}
            </AnimatePresence>
            {loaded && shows.length === 0 && (
              <p className="px-6 py-8 text-center text-sm text-muted-foreground">
                No shows yet. Upload a set to start mining clips.
              </p>
            )}
          </div>

          {/* Account footer */}
          <div className="border-t border-border p-4" data-testid="account-footer">
            <div className="flex items-center gap-3">
              {user?.picture ? (
                <img src={user.picture} alt={user.name} className="h-9 w-9 rounded-full border border-border object-cover" />
              ) : (
                <div className="grid h-9 w-9 place-items-center rounded-full border border-border bg-secondary font-display text-sm text-muted-foreground">
                  {(user?.name || user?.email || "?").charAt(0).toUpperCase()}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate font-body text-sm text-white">{user?.name || user?.email}</p>
                <span
                  data-testid="plan-badge"
                  className={`mt-0.5 inline-flex items-center gap-1 font-mono text-[9px] font-bold uppercase tracking-widest ${
                    isPro ? "text-accent" : "text-muted-foreground"
                  }`}
                >
                  {isPro && <Crown className="h-3 w-3" />}
                  {isPro ? "Pro" : "Free plan"}
                </span>
              </div>
              <button data-testid="logout-btn" onClick={logout} title="Sign out"
                      className="text-muted-foreground transition-colors hover:text-primary">
                <LogOut className="h-4 w-4" />
              </button>
            </div>
            {!isPro && (
              <button
                data-testid="sidebar-upgrade-btn"
                onClick={() => openUpgrade("Unlock unlimited shows, no watermark, 1080p and bulk downloads.")}
                className="mt-3 flex w-full items-center justify-center gap-2 bg-accent px-4 py-2.5 font-mono text-[11px] font-bold uppercase tracking-widest text-black transition-transform hover:-translate-y-0.5"
              >
                <Crown className="h-3.5 w-3.5" /> Upgrade to Pro
              </button>
            )}
          </div>
        </aside>

        {/* Main */}
        <main className="relative flex-1 overflow-y-auto px-8 py-10 lg:px-12">
          {view === "profile" ? (
            <ProfileView />
          ) : view === "bestof" ? (
            <BestOfView />
          ) : view === "schedule" ? (
            <SchedulerView />
          ) : view === "roster" ? (
            <RosterView />
          ) : selected ? (
            <ShowView showId={selected} onChanged={refresh} />
          ) : (
            <EmptyState hasShows={shows.length > 0} onTeach={() => setView("profile")} />
          )}
        </main>
      </div>
    </div>
  );
}

const EmptyState = ({ hasShows, onTeach }) => (
  <div className="relative flex min-h-[80vh] flex-col items-center justify-center overflow-hidden text-center">
    <div
      className="pointer-events-none absolute inset-0 opacity-20"
      style={{
        backgroundImage: "url('https://images.pexels.com/photos/9547593/pexels-photo-9547593.jpeg')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        maskImage: "radial-gradient(circle at center, black, transparent 72%)",
        WebkitMaskImage: "radial-gradient(circle at center, black, transparent 72%)",
      }}
    />
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative z-[2] max-w-2xl"
    >
      <div className="mx-auto mb-6 grid h-16 w-16 place-items-center border border-border bg-card">
        <Mic2 className="h-7 w-7 text-primary" />
      </div>
      <p className="font-mono text-xs uppercase tracking-[0.35em] text-accent">Turn full sets into viral clips</p>
      <h2 className="mt-4 font-display text-5xl leading-none tracking-wide text-white sm:text-7xl">
        MINE THE FUNNY
      </h2>
      <p className="mx-auto mt-5 max-w-md font-body text-muted-foreground">
        Drop a full-length live show. Whisper transcribes it, GPT-5.5 hunts down the punchlines,
        and each moment gets cut into a captioned, ready-to-post clip.
      </p>
      <p className="mt-8 font-mono text-xs text-muted-foreground">
        {hasShows ? "← Pick a show from your archive" : "← Upload your first set to begin"}
      </p>
      {!hasShows && (
        <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
          <a
            data-testid="empty-watch-demo"
            href="/demo"
            className="flex items-center gap-2 border border-accent bg-accent/10 px-5 py-3 font-body text-sm font-semibold text-accent transition-colors hover:bg-accent hover:text-black"
          >
            <PlayCircle className="h-4 w-4" /> See finished clips (demo)
          </a>
          <button
            data-testid="empty-teach-dna"
            onClick={onTeach}
            className="flex items-center gap-2 border border-border px-5 py-3 font-body text-sm text-white transition-colors hover:border-accent hover:text-accent"
          >
            <Dna className="h-4 w-4" /> Teach your Style DNA first
          </button>
        </div>
      )}
    </motion.div>
  </div>
);
